import React, { useEffect, useRef, useState } from "react";
import { Check, Download, FileText, Lock, Shield, Upload, User, Box } from "lucide-react";
import { Sheet } from "./PartnerForm";
import { Spinner } from "./ui/kit";
import api, { apiError } from "../lib/api";
import { fmtDate } from "../lib/format";

/** Převod klienta souborem (deska 20). Export dělá firma, import subdodavatel ve své firmě. */

const btnHlavni = "tap w-full h-12 md:h-[46px] rounded-full bg-accent text-white text-[16px] md:text-[14.5px] font-700 inline-flex items-center justify-center gap-2 disabled:opacity-45 disabled:cursor-not-allowed";
const btnVedlejsi = "tap w-full h-12 md:h-[46px] rounded-full bg-white shadow-[inset_0_0_0_1px_#e0deda] text-[16px] md:text-[14.5px] font-700 text-ink";

function KlientMini({ k }) {
  const ini = (k.display_name || "?").split(/\s+/).slice(0, 2).map((x) => x[0]).join("").toUpperCase();
  return (
    <div className="flex items-center gap-3 px-3.5 py-3 rounded-[16px] bg-surface">
      <span className="h-10 w-10 rounded-full bg-ink text-white font-800 text-[14px] inline-flex items-center justify-center shrink-0">{ini}</span>
      <span className="min-w-0">
        <span className="block t-telo md:text-[15px] font-700 text-ink truncate">{k.display_name}</span>
        <span className="block t-meta text-muted truncate">{[k.mesto, k.telefon, k.email].filter(Boolean).join(" · ")}</span>
      </span>
    </div>
  );
}

function Prepinac({ zap }) {
  return (
    <span className={`relative h-6 w-10 rounded-full shrink-0 transition-colors ${zap ? "bg-success" : "bg-[#d5d3ce]"}`}>
      <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 motion-reduce:transition-none ${zap ? "translate-x-4" : ""}`} />
    </span>
  );
}

function RadekDat({ Ikona, nazev, pod, zap, onClick, zamek, testid }) {
  const obsah = (
    <>
      <span className="h-9 w-9 rounded-[11px] bg-surface text-ink inline-flex items-center justify-center shrink-0"><Ikona size={17} strokeWidth={1.9} /></span>
      <span className="flex-1 min-w-0">
        <span className="block t-telo md:text-[14.5px] font-700 text-ink">{nazev}</span>
        <span className="block t-meta text-muted">{pod}</span>
      </span>
      {zamek ? <Lock size={16} className="text-[#b4b7bc] mr-2 shrink-0" /> : <Prepinac zap={zap} />}
    </>
  );
  const cls = "w-full flex items-center gap-3 py-2.5 px-1 border-t border-line-soft text-left";
  return zamek ? <div className={cls}>{obsah}</div>
    : <button type="button" onClick={onClick} aria-pressed={zap} data-testid={testid} className={`${cls} tap hover:bg-inputbg`}>{obsah}</button>;
}

const pocet = (n, a, b, c) => `${n} ${n === 1 ? a : n >= 2 && n <= 4 ? b : c}`;

export function ExportKlienta({ clientId, onClose }) {
  const [info, setInfo] = useState(null);
  const [zarizeni, setZarizeni] = useState(true);
  const [historie, setHistorie] = useState(false);
  const [souhlas, setSouhlas] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const [hotovo, setHotovo] = useState(null);

  useEffect(() => {
    api.get(`/api/prevod/klient/${clientId}`).then(({ data }) => setInfo(data))
      .catch((e) => setChyba(apiError(e.response?.data?.detail) || e.message));
  }, [clientId]);

  const stahnout = async () => {
    if (!souhlas) return setChyba("Nejdřív potvrď souhlas klienta.");
    setBusy(true); setChyba("");
    try {
      const { data } = await api.post(`/api/prevod/klient/${clientId}/export`, { souhlas, zarizeni, historie });
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = data.soubor; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setHotovo({ soubor: data.soubor, co: ["základní údaje", zarizeni && pocet(info.zarizeni, "zařízení", "zařízení", "zařízení"),
        historie && pocet(info.zapisy, "zápis", "zápisy", "zápisů")].filter(Boolean).join(", ") });
    } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };

  return (
    <Sheet titulek="Export klienta" onClose={onClose} testid="export-klienta">
      <div className="p-5 md:p-6 space-y-4">
        <p className="t-meta text-muted -mt-1">Soubor pošleš subdodavateli, ten si klienta naimportuje do své firmy.</p>
        {!info ? <div className="py-6 flex justify-center">{chyba ? <span className="t-meta text-danger">{chyba}</span> : <Spinner />}</div>
          : hotovo ? (
            <div className="space-y-4 rise" data-testid="export-hotovo">
              <div className="flex items-center gap-3 p-3.5 rounded-[16px] bg-success-bg">
                <span className="h-11 w-11 rounded-[12px] bg-white text-success inline-flex items-center justify-center shrink-0"><FileText size={20} /></span>
                <span className="min-w-0">
                  <span className="block t-telo md:text-[14.5px] font-700 text-ink truncate">{hotovo.soubor}</span>
                  <span className="block t-meta text-success">Staženo · {hotovo.co}</span>
                </span>
              </div>
              <div className="flex gap-2.5 items-start t-meta md:text-[13.5px] text-sec leading-relaxed">
                <Shield size={17} className="text-accent shrink-0 mt-0.5" />
                <span>Soubor obsahuje osobní údaje. Pošli ho jen subdodavateli a pak ho smaž. <b className="text-ink">Do historie klienta jsme zapsali</b>, kdo a kdy ho exportoval.</span>
              </div>
              <button type="button" className={btnVedlejsi} onClick={onClose}>Hotovo</button>
            </div>
          ) : (
            <>
              <KlientMini k={info.klient} />
              <div>
                <span className="label-up text-muted block mb-1.5">Co poslat</span>
                <RadekDat Ikona={User} nazev="Základní údaje" pod="Jméno, adresa, telefon, e-mail, IČO" zamek />
                <RadekDat Ikona={Box} nazev="Zařízení" testid="export-zarizeni"
                  pod={info.zarizeni ? `${pocet(info.zarizeni, "zařízení", "zařízení", "zařízení")} · výrobní čísla a servisní interval` : "klient zatím nemá zařízení"}
                  zap={zarizeni || historie} onClick={() => { setZarizeni((v) => !v); if (zarizeni) setHistorie(false); }} />
                <RadekDat Ikona={FileText} nazev="Servisní historie" testid="export-historie"
                  pod={info.zapisy ? `${pocet(info.zapisy, "zápis", "zápisy", "zápisů")} · data a výsledky, bez cen` : "zatím žádné hotové zápisy"}
                  zap={historie} onClick={() => { setHistorie((v) => !v); if (!historie) setZarizeni(true); }} />
              </div>
              <div className="flex gap-2 items-center t-meta text-muted -mt-1">
                <Lock size={13} className="shrink-0" />Ceny, nabídky, faktury a interní poznámky se nikdy neposílají.
              </div>
              <button type="button" onClick={() => { setSouhlas((v) => !v); setChyba(""); }} aria-pressed={souhlas} data-testid="export-souhlas"
                className={`tap w-full flex gap-3 items-start px-3.5 py-3 rounded-[14px] text-left transition-colors ${souhlas ? "bg-success-bg" : "bg-surface"}`}>
                <span className={`h-[22px] w-[22px] rounded-[7px] inline-flex items-center justify-center shrink-0 mt-px ${
                  souhlas ? "bg-success text-white" : "bg-white text-transparent shadow-[inset_0_0_0_1.5px_#d5d3ce]"}`}><Check size={14} strokeWidth={3} /></span>
                <span className="t-telo md:text-[13.5px] text-ink leading-snug"><b>Klient souhlasí</b>, aby jeho údaje měla i firma, které soubor posílám.</span>
              </button>
              {chyba && <p className="t-meta text-danger">{chyba}</p>}
              <button type="button" className={btnHlavni} disabled={!souhlas || busy} onClick={stahnout} data-testid="export-stahnout">
                {busy ? <Spinner /> : <><Download size={17} strokeWidth={2.2} /> Stáhnout soubor</>}
              </button>
              {info.historie?.length > 0 && (
                <p className="t-meta text-muted" data-testid="export-historie-zaznam">
                  Naposledy: {info.historie[0].typ === "EXPORT" ? "exportoval" : "importoval"} {info.historie[0].user_name} {fmtDate(info.historie[0].created_at)}
                </p>
              )}
            </>
          )}
      </div>
    </Sheet>
  );
}

export function ImportKlienta({ onClose, onDone }) {
  const [data, setData] = useState(null);
  const [nahled, setNahled] = useState(null);
  const [rezim, setRezim] = useState("novy");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const [vysledek, setVysledek] = useState(null);
  const [nadZonou, setNadZonou] = useState(false);
  const input = useRef(null);

  const nacist = async (soubor) => {
    if (!soubor) return;
    setChyba(""); setBusy(true);
    try {
      if (soubor.size > 5 * 1024 * 1024) throw new Error("Soubor je příliš velký.");
      let obsah;
      try { obsah = JSON.parse(await soubor.text()); } catch { throw new Error("Tohle není soubor klienta z ProfitPilotu (.ppklient)."); }
      const { data: n } = await api.post("/api/prevod/import/nahled", { data: obsah });
      setData(obsah); setNahled(n); setRezim(n.duplicita ? "doplnit" : "novy");
    } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };

  const importovat = async () => {
    setBusy(true); setChyba("");
    try {
      const { data: v } = await api.post("/api/prevod/import", {
        data, rezim, client_id: rezim === "doplnit" ? nahled.duplicita.client_id : null });
      setVysledek(v);
    } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };

  return (
    <Sheet titulek="Import klienta" onClose={onClose} testid="import-klienta">
      <div className="p-5 md:p-6 space-y-4">
        {vysledek ? (
          <div className="space-y-4 rise" data-testid="import-hotovo">
            <span className="h-[52px] w-[52px] rounded-full bg-success text-white inline-flex items-center justify-center"><Check size={26} strokeWidth={3} /></span>
            <div className="text-[20px] font-800 text-ink">{rezim === "doplnit" ? `Doplněno k ${nahled.duplicita.display_name}` : `${nahled.klient.display_name} naimportován`}</div>
            <p className="t-meta md:text-[13.5px] text-sec leading-relaxed">
              {[vysledek.zarizeni && pocet(vysledek.zarizeni, "nové zařízení", "nová zařízení", "nových zařízení"),
                vysledek.zapisy && pocet(vysledek.zapisy, "servisní zápis", "servisní zápisy", "servisních zápisů")].filter(Boolean).join(", ") || "Údaje klienta jsou u tebe"}.
              {" "}Klientovi teď můžeš přidávat faktury, nabídky a další technologie. Původní firma o tvých datech nic neuvidí.
            </p>
            <button type="button" className={btnHlavni.replace("bg-accent", "bg-ink")} onClick={() => onDone(vysledek.client_id)} data-testid="import-otevrit">
              Otevřít klienta
            </button>
          </div>
        ) : !nahled ? (
          <>
            <p className="t-meta text-muted -mt-1">Klient z jiné firmy do tvé vlastní.</p>
            <button type="button" onClick={() => input.current?.click()} data-testid="import-zona"
              onDragOver={(e) => { e.preventDefault(); setNadZonou(true); }} onDragLeave={() => setNadZonou(false)}
              onDrop={(e) => { e.preventDefault(); setNadZonou(false); nacist(e.dataTransfer.files?.[0]); }}
              className={`tap w-full py-8 md:py-9 px-4 rounded-[18px] border-[1.5px] border-dashed flex flex-col items-center gap-2.5 transition-colors ${
                nadZonou ? "border-accent bg-[#fff1e8]" : "border-[#d5d3ce] bg-surface"}`}>
              <span className="h-12 w-12 rounded-full bg-white shadow-sm inline-flex items-center justify-center text-ink">{busy ? <Spinner /> : <Upload size={20} />}</span>
              <span className="t-telo md:text-[15px] font-700 text-ink"><span className="md:hidden">Vybrat soubor</span><span className="hidden md:inline">Přetáhni sem soubor nebo ho vyber</span></span>
              <span className="t-meta text-muted">.ppklient z jiné firmy v ProfitPilotu</span>
            </button>
            <input ref={input} type="file" accept=".ppklient,application/json" className="hidden" data-testid="import-soubor"
              onChange={(e) => { nacist(e.target.files?.[0]); e.target.value = ""; }} />
            {chyba && <p className="t-meta text-danger" data-testid="import-chyba">{chyba}</p>}
            <p className="t-meta text-muted leading-relaxed">Soubor ti pošle firma, pro kterou klienta servisuješ. Nic se nezaloží, dokud nepotvrdíš náhled.</p>
          </>
        ) : (
          <div className="space-y-4 rise" data-testid="import-nahled">
            <p className="t-meta text-muted -mt-1">
              Od <b className="text-ink">{nahled.od_firmy}</b>{nahled.exportoval ? ` · exportoval ${nahled.exportoval}` : ""}{nahled.exportovano ? ` · ${fmtDate(nahled.exportovano)}` : ""}
            </p>
            <KlientMini k={nahled.klient} />
            <div>
              <span className="label-up text-muted block mb-1">Co se založí</span>
              {[rezim === "doplnit" ? `Doplní se prázdné údaje u ${nahled.duplicita.display_name}` : `Klient ${nahled.klient.display_name}`,
                nahled.zarizeni.length > 0 && `${pocet(nahled.zarizeni.length, "zařízení", "zařízení", "zařízení")} — ${nahled.zarizeni.slice(0, 3).join(", ")}${nahled.zarizeni.length > 3 ? "…" : ""}`,
                nahled.zapisy > 0 && pocet(nahled.zapisy, "servisní zápis", "servisní zápisy", "servisních zápisů"),
              ].filter(Boolean).map((t) => (
                <div key={t} className="flex items-center gap-2.5 py-2 border-t border-line-soft t-telo md:text-[14px] text-ink">
                  <Check size={15} strokeWidth={3} className="text-success shrink-0" /><span className="min-w-0">{t}</span>
                </div>
              ))}
            </div>
            {nahled.z_vlastni_firmy && (
              <p className="t-meta text-[#7a5300] bg-[#fff6e5] rounded-[14px] px-3.5 py-3">Soubor je z tvé vlastní firmy — klienta už nejspíš máš.</p>
            )}
            {nahled.duplicita && (
              <div className="px-3.5 py-3 rounded-[14px] bg-[#fff6e5] text-[#7a5300] t-meta md:text-[13.5px] leading-snug" data-testid="import-duplicita">
                <b>{nahled.duplicita.display_name} už u tebe je</b> (stejné {nahled.duplicita.podle}). Co s tím?
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {[["doplnit", "Doplnit ke stávajícímu"], ["novy", "Založit nového"]].map(([v, t]) => (
                    <button key={v} type="button" onClick={() => setRezim(v)} aria-pressed={rezim === v} data-testid={`import-rezim-${v}`}
                      className={`tap md:min-h-0 h-9 md:h-[34px] px-3.5 rounded-full text-[13px] font-700 ${rezim === v ? "bg-[#7a5300] text-white" : "bg-white text-[#7a5300]"}`}>{t}</button>
                  ))}
                </div>
              </div>
            )}
            {chyba && <p className="t-meta text-danger">{chyba}</p>}
            <button type="button" className={btnHlavni} onClick={importovat} disabled={busy} data-testid="import-potvrdit">
              {busy ? <Spinner /> : "Importovat klienta"}
            </button>
          </div>
        )}
      </div>
    </Sheet>
  );
}
